import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retryWhen, tap } from 'rxjs/operators';
import { AuthGuardService } from './shared/service/auth-guard/auth-guard.service';
import { ToastrService } from 'ngx-toastr';
import { genericRetryStrategy } from './shared/helper/rxjs-utils';
import { Router } from '@angular/router';
import * as AppActions from './store/app/actions';
import { Store } from '@ngrx/store';
import * as _ from 'lodash';
import { IotDeviceManagementPortalAppState } from './store/iotDeviceManagementPortalApp.reducer';
import { JWTTokenService } from './shared/service/jwt-token-service';
import { environment } from '../environments/environment';

@Injectable()
export class AppHttpInterceptor implements HttpInterceptor {
  private jwtToken: string;

  constructor(
    public authGuardService: AuthGuardService,
    private jwtTokenService: JWTTokenService,
    private toast: ToastrService,
    private store: Store<IotDeviceManagementPortalAppState>,
    public router: Router,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isSharedRoute = window.location.hash.includes('/shared/dashboard');

    let sharedToken = sessionStorage.getItem('shared_dashboard_token');

    if (isSharedRoute && !sharedToken) {
      const match = window.location.hash.match(/\/shared\/dashboard\/(.+)/);
      if (match) {
        sharedToken = match[1].split('?')[0]; // Extract token, ignoring any query params
        sessionStorage.setItem('shared_dashboard_token', sharedToken);
      }
    }

    this.jwtToken = sharedToken || this.jwtTokenService.getJwtCookie(environment.sessionTokenName);

    if (!this.jwtToken && !isSharedRoute) {
      this.authGuardService.redirectToLogin();
      return throwError('No valid token found');
    }

    const clonedRequest = req.clone({
      setHeaders: { Authorization: `Bearer ${this.jwtToken}` },
    });

    return next.handle(clonedRequest).pipe(
      tap(() => {}),
      retryWhen(
        genericRetryStrategy({
          maxRetryAttempts: 5,
          excludedStatusCodes: [400, 401, 500],
        }),
      ),
      catchError((err: any) => {
        let errorMessage = 'Error';
        if (err instanceof HttpErrorResponse) {
          switch (err.status) {
            case 400:
              if (typeof err.error.message !== 'undefined') {
                errorMessage = err.error.message.message;
              } else if (typeof err.message !== 'undefined' && [null, ''].indexOf(err.message) === -1) {
                errorMessage = err.message;
              }
              this.toast.error(errorMessage, 'Failed');
              break;
            case 401:
              if (!isSharedRoute) {
                this.authGuardService.redirectToLogin();
              } else {
                this.toast.error('Your Link is expired or invalid.', 'Authorization Error');
              }
              break;
            case 403:
              this.router.navigate(['/error/forbidden']);
              break;
            case 0:
            case 504:
              break;
            case 500:
              if (err.error?.message === 'jwt expired') {
                this.toast.warning('Your Authorization is expired. redirecting...', 'Authorization Error');
              }
              break;
            default:
              if (typeof err.message !== 'undefined' && [null, ''].indexOf(err.message) === -1) {
                try{
                  errorMessage = JSON.stringify(err.error.message.constraints);
                }catch (e) {
                  errorMessage = err.message;
                }

              }
              this.toast.error(errorMessage, 'Failed');
              break;
          }
        }

        const errorUrl = _.get(err, 'error.target.__zone_symbol__xhrURL', false);

        if (errorUrl && err.status === 0) {
          err.message = err.message.replace(/ *\([^)]*\) */g, ` (${errorUrl})`);
        }
        this.store.dispatch(new AppActions.HideTopLoader());
        this.store.dispatch(new AppActions.HideLoader());
        return throwError(err);
      }),
    );
  }
}
